/**
 * Los movimientos de stock que se cargan a mano.
 *
 * DOS FORMAS DE ESCRIBIR EL MISMO MOVIMIENTO
 * ------------------------------------------
 * INGRESO y SALIDA preguntan cuantas unidades se mueven. CONTEO pregunta
 * cuantas hay, y hace la resta. Es la misma operacion contada al reves, y existe
 * porque quien mira un estante no piensa "entran dos": piensa "hay siete". La
 * traduccion la hace `ajustePorConteo()`, no la cabeza de quien esta contando
 * con un cliente esperando.
 *
 * INGRESO: LA OPERACION QUE CAMBIA EL PRECIO SIN QUE PAREZCA
 * ----------------------------------------------------------
 * Cargar una compra parece una tarea de deposito: llegaron seis, sumalos. Pero
 * si el ingreso trae costo, por defecto ese costo pasa a ser el costo de
 * reposicion del producto, y el trigger de precios recalcula el precio de venta
 * en el mismo commit. Se entra a anotar cuantas cajas llegaron y se sale con la
 * lista de precios cambiada.
 *
 * Por eso, apenas se escribe un costo, el dialogo muestra el precio de venta de
 * ANTES y el de DESPUES, calculados con `calcularPrecio()` — la misma funcion
 * que replica el SQL. Y deja destildar la actualizacion, que es lo que hay que
 * hacer en un ajuste de inventario.
 *
 * SALIDA: LA QUE NO ESTA
 * ----------------------
 * No hay tipo VENTA. Una salida por venta la genera `emitir_venta` junto con el
 * comprobante, en la misma transaccion; cargarla suelta descontaria el stock dos
 * veces, una por el ajuste y otra por la venta. El backend tampoco la acepta.
 * Esto se dice en el dialogo, porque si no la pregunta vuelve.
 *
 * Los dos terminan en un paso de revision con el stock que va a quedar. El
 * numero que importa no es el que se escribe (la cantidad) sino el que queda.
 */
import { useEffect, useMemo, useState } from "react";
import { ArrowLeft, ArrowRight, Plus } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { esReferenciaUsada, formatearNumero, type Moneda } from "@tora/shared";

import { Importe, InputImporte } from "@/components/money";
import { Aviso } from "@/components/ui/aviso";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { TituloSeccion } from "@/components/ui/titulo-seccion";
import { cn } from "@/lib/utils";
import {
  aNumero,
  type Encargo,
  type IngresoStock,
  type Producto,
  type SalidaStock,
  type Socio,
} from "@/lib/tipos";

import { GrupoCampo } from "@/components/ui/campo";
import { fichasDeColorDelModelo, modeloDeLaFichaDeColor } from "@/components/iphones/entrada";
import { esIphoneSellado } from "@/components/iphones/logica";
import { ChipsSocio } from "@/components/socios/ChipsSocio";

import { coloresDelModelo } from "./colores";
import { Campo, CampoCantidad, Casilla, SelectorProducto, mostrarCantidad } from "./Campos";
import {
  RepartoPorColor,
  faltaParaRepartir,
  soloLasQueVan,
  type UnidadesDeColor,
} from "./RepartoPorColor";
import {
  filtrarPorTexto,
  TIPOS_INGRESO,
  TIPOS_SALIDA,
  ajustePorConteo,
  costoComparable,
  impactoDelIngreso,
  salidaSuperaElStock,
  stockResultante,
  type AjustePorConteo,
  type TipoIngreso,
  type TipoSalida,
} from "./logica";

// ─────────────────────────────────────────────────────────────────────────────
// Ingreso
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Qué productos ofrece el ingreso, según el camino elegido en «¿Qué entró?».
 *
 * Un usado no se ofrece NUNCA: es un teléfono, tiene 0 o 1 unidad, y entra
 * con «Cargar usado» (o «Ya lo tengo» si estaba a pedido). La base lo frena
 * igual (0092/0097), pero recién al confirmar.
 */
export type AlcanceIngreso = "todo" | "sellados" | "otros";

export function productosDelAlcance(
  productos: ReadonlyArray<Producto>,
  alcance: AlcanceIngreso,
): Producto[] {
  // Los modelos que tienen su fila sin color. Si un modelo no la tiene (el 18
  // Pro Max quedo con sus dos fichas con color), sus fichas de color se
  // ofrecen igual: si no, «iPhone sellado» decia «No encontré» y el equipo
  // vendido a pedido no tenia por donde entrar.
  const conFilaDeModelo = new Set(
    productos.filter((p) => !p.es_usado && esIphoneSellado(p) && !p.color).map((p) => p.nombre),
  );
  return productos.filter((p) => {
    if (p.es_usado) return false;
    if (alcance === "sellados") {
      return esIphoneSellado(p) && (!p.color || !conFilaDeModelo.has(p.nombre));
    }
    if (alcance === "otros") return !esIphoneSellado(p);
    return true;
  });
}

/**
 * Los encargos que esperan lo que se está ingresando.
 *
 * Lo vendido a pedido deja un encargo en la fila que se vendió: la del modelo
 * (lo que se ofrece en «iPhone sellado») o, si se vendió una unidad de color,
 * la de ese color. Por eso, con la fila del modelo elegida, también cuentan
 * las de sus colores; y con una ficha de color elegida, cuenta la de su
 * modelo (el caso normal: el sellado se vende desde el modelo y el color va en
 * la nota, que el aviso muestra). Antes solo se miraba del modelo hacia los
 * colores, y el Midnight que llegaba por su ficha entraba como stock libre.
 * Los encargos de OTROS colores del mismo modelo no cuentan: son otro equipo.
 */
export function encargosQueEsperan(
  producto: Producto | null,
  productos: ReadonlyArray<Producto>,
  encargos: ReadonlyArray<Encargo>,
): Encargo[] {
  if (!producto || encargos.length === 0) return [];
  const ids = new Set<number>([
    producto.id,
    ...fichasDeColorDelModelo(producto, productos).map((p) => p.id),
  ]);
  const modelo = modeloDeLaFichaDeColor(producto, productos);
  if (modelo) ids.add(modelo.id);
  return encargos.filter((e) => e.recibido_at === null && ids.has(e.producto_id));
}

export interface DialogIngresoProps {
  abierto: boolean;
  onAbiertoChange: (abierto: boolean) => void;
  productos: ReadonlyArray<Producto>;
  /** Producto preseleccionado (por ejemplo, desde una alerta). */
  productoId?: number | null;
  cotizacion: number;
  guardando?: boolean;
  /**
   * El `reparto` solo viaja cuando lo que se eligio es la FILA DE LISTA DE
   * PRECIOS de un iPhone sellado. Esa fila no recibe stock —dice cuanto sale el
   * modelo y en que colores viene—, asi que la pantalla ingresa contra la
   * unidad de cada color: la busca y, si no existe, la crea.
   *
   * Es una lista y no un color porque entran tres y pueden ser de tres colores
   * distintos. La cantidad de cada renglon ya viene repartida y suma el total
   * de `datos.cantidad`.
   */
  onConfirmar: (datos: IngresoStock, reparto?: ReadonlyArray<UnidadesDeColor>) => void;
  /** El camino elegido en «¿Qué entró?». Sin él, todo menos los usados. */
  alcance?: AlcanceIngreso;
  /**
   * Lo que entró no está en el catálogo: se da de alta ahí mismo. Sin esto
   * la opción no aparece. Recibe lo que se estaba buscando, para que el alta
   * no arranque con el nombre en blanco.
   */
  onCrearNuevo?: (buscado: string) => void;
  /**
   * Los encargos que todavía no llegaron (POR_RECIBIR). Si lo que se elige es
   * lo que un cliente ya pagó, el diálogo lo dice antes de sumar stock.
   */
  encargos?: ReadonlyArray<Encargo>;
  /** «Es ese»: se marca recibido con el costo, sin sumar stock libre. */
  onRecibirEncargo?: (encargo: Encargo, costo: number, moneda: Moneda) => void;
  recibiendo?: boolean;
}

export function DialogIngreso({
  abierto,
  onAbiertoChange,
  productos,
  productoId = null,
  cotizacion,
  guardando = false,
  onConfirmar,
  alcance = "todo",
  onCrearNuevo,
  encargos = [],
  onRecibirEncargo,
  recibiendo = false,
}: DialogIngresoProps): JSX.Element {
  const [paso, setPaso] = useState<"carga" | "revision">("carga");
  const [producto, setProducto] = useState<Producto | null>(null);
  const [buscado, setBuscado] = useState("");
  const [tipo, setTipo] = useState<TipoIngreso>("COMPRA");
  const [cantidad, setCantidad] = useState<number | null>(null);
  const [costo, setCosto] = useState<number | null>(null);
  const [costoMoneda, setCostoMoneda] = useState<Moneda>("USD");
  const [actualizaCosto, setActualizaCosto] = useState(true);
  const [referencia, setReferencia] = useState("");
  const [observaciones, setObservaciones] = useState("");
  const [reparto, setReparto] = useState<ReadonlyArray<UnidadesDeColor>>([]);
  const [esOtraUnidad, setEsOtraUnidad] = useState(false);

  /*
   * UN USADO NO ENTRA POR ACA.
   *
   * Si es un equipo, es un solo telefono (stock 0 o 1): el que entro lleva su
   * ficha, con «Cargar usado». Si es un precio de referencia, no lleva stock.
   * La base rechaza las dos cosas; ofrecerlo era dejar llenar el formulario
   * entero para comerse el error al guardar.
   *
   * El resto de lo que se ofrece depende del camino elegido en «¿Qué entró?»:
   * "sellados" solo muestra las filas de modelo (lista de precios), "otros"
   * saca los iPhone (esos van por Compras), y "todo" es el default de siempre.
   */
  const ofrecidos = useMemo(() => productosDelAlcance(productos, alcance), [productos, alcance]);

  useEffect(() => {
    if (!abierto) return;
    const pre = productoId === null ? null : (ofrecidos.find((p) => p.id === productoId) ?? null);
    setPaso("carga");
    setProducto(pre);
    setBuscado("");
    setTipo("COMPRA");
    setCantidad(null);
    setCosto(null);
    setCostoMoneda(pre?.costo_moneda ?? "USD");
    setActualizaCosto(true);
    setReferencia("");
    setObservaciones("");
    setReparto([]);
    setEsOtraUnidad(false);
  }, [abierto, productoId, ofrecidos]);

  /**
   * La fila que se eligio es el MODELO y no una unidad: un iPhone sellado sin
   * color. Es la lista de precios —cuanto sale el 17 Pro y en que colores
   * viene—, y ahi "entraron tres 17 Pro" sin decir de cuales es justo lo que
   * despues nadie puede contestar por telefono.
   *
   * ANTES ESTO PEDIA ADEMAS QUE LA FILA ESTUVIERA EN CERO. El argumento era que
   * una ficha sin color que ya tenia unidades se venia usando como fila de
   * stock de antes de esta distincion, y frenarle un ingreso era romperle la
   * operacion a alguien por una regla nueva.
   *
   * El argumento valia cuando la unica respuesta posible era un color para todo
   * el ingreso: ahi si, o se elegia uno o se frenaba. Con el repartidor no se
   * frena nada, se pregunta — y la condicion habia pasado a ser lo contrario de
   * lo que buscaba: la fila con unidades sin color es la que la lista marca
   * como incompleta, y era justo la unica a la que no se le podia poner el
   * color desde ninguna pantalla.
   */
  const esModelo = producto !== null && esIphoneSellado(producto) && !producto.color;
  const coloresModelo = producto ? coloresDelModelo(producto.nombre) : [];

  const infoTipo = TIPOS_INGRESO.find((t) => t.tipo === tipo);

  // Un ajuste de inventario no es una senal de precio: aparecieron unidades que
  // no estaban contadas, no se compro nada. Cambiar de tipo mueve el default.
  function cambiarTipo(nuevo: TipoIngreso): void {
    setTipo(nuevo);
    setActualizaCosto(TIPOS_INGRESO.find((t) => t.tipo === nuevo)?.actualizaCosto ?? false);
  }

  const impacto = useMemo(
    () =>
      producto
        ? impactoDelIngreso({
            producto,
            costoUnitario: costo,
            costoMoneda,
            actualizaCosto,
            cotizacion,
          })
        : null,
    [producto, costo, costoMoneda, actualizaCosto, cotizacion],
  );

  const resultante = producto
    ? stockResultante(aNumero(producto.stock_actual), cantidad ?? 0, 1)
    : null;

  /**
   * Un modelo sin lineup cargado no puede repartir nada: la columna Color
   * muestra «—» porque el modelo no esta en `catalogo/colores.ts`, y eso no se
   * arregla desde esta pantalla. Antes que trabar el ingreso, entra como
   * entraba: contra la fila, sin color. Queda marcado como incompleto, que es
   * la senal correcta — lo que falta es el lineup, no el dato.
   */
  const puedeRepartir = esModelo && coloresModelo.length > 0;

  /*
   * LO QUE LLEGA PUEDE SER DE UN CLIENTE.
   *
   * Antes el ingreso no miraba los encargos: el iPhone vendido a pedido que
   * llegaba entraba como stock libre, el mostrador lo ofrecía y el encargo
   * seguía en «Falta que llegue». El mismo equipo contado dos veces, y vendible
   * a otro cliente. Ahora, si hay encargos esperando lo elegido, se ofrece
   * marcarlo recibido (no suma stock libre) y, para sumarlo igual, hay que
   * decir que es otra unidad.
   */
  const esperando = encargosQueEsperan(producto, productos, encargos);
  const hayEncargos = esperando.length > 0;

  const listo = Boolean(
    producto &&
    cantidad !== null &&
    cantidad > 0 &&
    (!puedeRepartir || faltaParaRepartir(reparto, cantidad) === null) &&
    (!hayEncargos || esOtraUnidad),
  );

  function confirmar(): void {
    if (!producto || cantidad === null) return;
    onConfirmar(
      {
        producto_id: producto.id,
        cantidad,
        tipo,
        // El costo y su moneda viajan siempre juntos: el backend rechaza uno sin
        // el otro y la base tiene el mismo check.
        ...(costo !== null ? { costo_unitario: costo, costo_moneda: costoMoneda } : {}),
        ...(costo !== null ? { actualiza_costo: actualizaCosto } : {}),
        ...(producto.proveedor_id !== null ? { proveedor_id: producto.proveedor_id } : {}),
        ...(referencia.trim() ? { referencia: referencia.trim() } : {}),
        ...(observaciones.trim() ? { observaciones: observaciones.trim() } : {}),
      },
      puedeRepartir ? soloLasQueVan(reparto) : undefined,
    );
  }

  return (
    <Dialog open={abierto} onOpenChange={onAbiertoChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {paso === "carga" ? "Ingreso de mercadería" : "Revisá antes de guardar"}
          </DialogTitle>
          <DialogDescription>
            {paso === "carga"
              ? "Suma unidades y deja rastro de quién, cuándo y por qué."
              : "Esto escribe un movimiento de stock. No se deshace con Ctrl+Z."}
          </DialogDescription>
        </DialogHeader>

        <DialogBody>
          {paso === "carga" ? (
            <>
              {alcance === "otros" ? (
                <Aviso tono="info" compacto>
                  ¿Tenés el remito o la factura? Cargalo en{" "}
                  <Link to="/compras" className="font-medium underline underline-offset-2">
                    Compras
                  </Link>
                  : suma el stock y queda en la cuenta del proveedor.
                </Aviso>
              ) : null}

              <SelectorProducto
                productos={ofrecidos}
                seleccionado={producto}
                onSeleccionar={(p) => {
                  setProducto(p);
                  setCostoMoneda(p.costo_moneda);
                  setReparto([]);
                  setEsOtraUnidad(false);
                }}
                onTexto={setBuscado}
                autoFocus
              />
              {/* Un iPhone sellado se ingresa por la fila de su memoria. Si esa
                  memoria todavia no tiene ficha, la busqueda decia «No
                  encontré» y nada mas: quien recibe la caja no sabia que
                  primero hay que cargarle costo y precio en Catalogo. */}
              {alcance === "sellados" &&
              !producto &&
              buscado.trim() !== "" &&
              filtrarPorTexto(ofrecidos, buscado).length === 0 ? (
                <Aviso tono="info" compacto>
                  Si es una memoria que todavía no tiene ficha, primero cargale costo y precio en{" "}
                  <Link to="/catalogo-interno" className="underline underline-offset-2">
                    Catálogo, en Sellados
                  </Link>{" "}
                  (el renglón de esa memoria dice «Falta») y después volvé a ingresarla acá.
                </Aviso>
              ) : null}
              {/* Lo que no aparece en la búsqueda se crea acá mismo, sin irse
                  a Catálogo y volver, con lo que se buscó como nombre. */}
              {onCrearNuevo && !producto ? (
                <Button
                  type="button"
                  variant="dashed"
                  onClick={() => onCrearNuevo(buscado)}
                  className="w-full"
                >
                  <Plus aria-hidden="true" />
                  ¿No está? Crear producto nuevo
                </Button>
              ) : null}

              {hayEncargos ? (
                <AvisoEncargos
                  encargos={esperando}
                  costo={costo}
                  moneda={costoMoneda}
                  recibiendo={recibiendo}
                  onRecibir={onRecibirEncargo}
                  esOtraUnidad={esOtraUnidad}
                  onEsOtraUnidadChange={setEsOtraUnidad}
                />
              ) : null}

              <Campo label="Tipo de ingreso" id="ing-tipo" ayuda={infoTipo?.ayuda}>
                <Select value={tipo} onValueChange={(v) => cambiarTipo(v as TipoIngreso)}>
                  <SelectTrigger id="ing-tipo" aria-label="Tipo de ingreso">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TIPOS_INGRESO.map((t) => (
                      <SelectItem key={t.tipo} value={t.tipo}>
                        {t.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Campo>

              <CampoCantidad
                label="Cantidad que entra"
                valor={cantidad}
                onValorChange={setCantidad}
                sufijo="u."
                requerido
                ayuda={
                  resultante && cantidad !== null
                    ? `Queda en ${mostrarCantidad(resultante.despues)} unidades.`
                    : undefined
                }
              />

              {/*
                Va DESPUES de la cantidad y no antes: no se puede repartir un
                total que todavia no se escribio.
              */}
              {puedeRepartir && cantidad !== null && cantidad > 0 ? (
                <GrupoCampo
                  label="¿Cuántas de cada color?"
                  ayuda="Esa fila es la lista de precios del modelo. Las unidades entran a la ficha de su color, que se crea sola si todavía no existe."
                  requerido
                >
                  <RepartoPorColor
                    colores={coloresModelo}
                    total={cantidad}
                    reparto={reparto}
                    onRepartoChange={setReparto}
                  />
                </GrupoCampo>
              ) : null}

              <Card padding="normal" className="space-y-3">
                <InputImporte
                  label="Costo unitario de esta compra"
                  valor={costo}
                  onValorChange={setCosto}
                  moneda={costoMoneda}
                  onMonedaChange={setCostoMoneda}
                  cotizacion={cotizacion}
                  ayuda="Opcional. Si lo dejás vacío, el costo del producto queda como estaba."
                />

                {costo !== null ? (
                  <>
                    <Casilla
                      label="Que este costo pase a ser el de reposición"
                      ayuda="Es lo que corresponde en una compra: lo que salió reponerlo hoy."
                      marcado={actualizaCosto}
                      onMarcadoChange={setActualizaCosto}
                    />
                    {impacto ? <AvisoImpacto impacto={impacto} cotizacion={cotizacion} /> : null}
                  </>
                ) : null}
              </Card>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Campo label="Remito o factura" id="ing-ref">
                  <Input
                    id="ing-ref"
                    value={referencia}
                    onChange={(e) => setReferencia(e.target.value)}
                    placeholder="A-0001-00012345"
                    autoComplete="off"
                  />
                </Campo>
                <Campo label="Observaciones" id="ing-obs">
                  <Input
                    id="ing-obs"
                    value={observaciones}
                    onChange={(e) => setObservaciones(e.target.value)}
                    placeholder="Lo que haya que aclarar"
                    autoComplete="off"
                  />
                </Campo>
              </div>
            </>
          ) : (
            <>
              <ResumenMovimiento
                producto={producto?.nombre ?? ""}
                operacion={`Ingreso · ${infoTipo?.label ?? tipo}`}
                cantidad={cantidad ?? 0}
                signo={1}
                resultante={resultante}
                porColor={
                  puedeRepartir
                    ? soloLasQueVan(reparto).map((r) => ({
                        color:
                          coloresModelo.find((c) => c.etiqueta.toUpperCase() === r.color)
                            ?.etiqueta ?? r.color,
                        cantidad: r.cantidad,
                      }))
                    : undefined
                }
              />
              {costo !== null ? (
                <Aviso tono="neutral" compacto>
                  Costo informado: <Importe monto={costo} moneda={costoMoneda} tamano="sm" /> por
                  unidad.{" "}
                  {actualizaCosto
                    ? "Pasa a ser el costo de reposición del producto."
                    : "No toca el costo del producto."}
                </Aviso>
              ) : null}
              {impacto ? <AvisoImpacto impacto={impacto} cotizacion={cotizacion} /> : null}
              {hayEncargos ? (
                <Aviso tono="warning" compacto>
                  Entra al stock libre aunque{" "}
                  {esperando.length === 1
                    ? "hay un encargo esperando"
                    : `hay ${esperando.length} encargos esperando`}{" "}
                  este equipo: lo dijiste vos, es otra unidad.
                </Aviso>
              ) : null}
            </>
          )}
        </DialogBody>

        <DialogFooter>
          {paso === "carga" ? (
            <>
              <Button type="button" variant="ghost" onClick={() => onAbiertoChange(false)}>
                Cancelar
              </Button>
              <Button type="button" disabled={!listo} onClick={() => setPaso("revision")}>
                Revisar
              </Button>
            </>
          ) : (
            <>
              <Button type="button" variant="ghost" onClick={() => setPaso("carga")}>
                <ArrowLeft aria-hidden="true" />
                Volver
              </Button>
              <Button type="button" cargando={guardando} onClick={confirmar}>
                Confirmar ingreso
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Los encargos que esperan lo elegido, cada uno con su «Es ese».
 *
 * El costo con que se marca recibido es el que se escribió en el ingreso; si
 * no se escribió ninguno, el estimado al vender (el mismo que propone
 * Por recibir). Sin ninguno de los dos, el botón espera el costo.
 */
function AvisoEncargos({
  encargos,
  costo,
  moneda,
  recibiendo,
  onRecibir,
  esOtraUnidad,
  onEsOtraUnidadChange,
}: {
  encargos: ReadonlyArray<Encargo>;
  costo: number | null;
  moneda: Moneda;
  recibiendo: boolean;
  onRecibir?: (encargo: Encargo, costo: number, moneda: Moneda) => void;
  esOtraUnidad: boolean;
  onEsOtraUnidadChange: (v: boolean) => void;
}): JSX.Element {
  const titulo =
    encargos.length === 1
      ? "Hay un encargo esperando este equipo"
      : `Hay ${encargos.length} encargos esperando este equipo`;
  return (
    <Aviso tono="warning" titulo={titulo} data-testid="aviso-encargos">
      <ul className="mt-1 space-y-2">
        {encargos.map((e) => {
          const costoUsado = costo ?? (e.costo_estimado == null ? null : aNumero(e.costo_estimado));
          const monedaUsada = costo !== null ? moneda : (e.moneda_estimada ?? "USD");
          return (
            <li key={e.id} className="flex flex-wrap items-center justify-between gap-2">
              <span>
                {e.cliente ?? "Sin cliente"} · {e.venta}
                {e.color ? ` · ${e.color}` : ""}
                {e.nota ? ` · ${e.nota}` : ""}
              </span>
              {onRecibir ? (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={costoUsado === null}
                  cargando={recibiendo}
                  title={costoUsado === null ? "Escribí el costo de esta compra" : undefined}
                  onClick={() => {
                    if (costoUsado !== null) onRecibir(e, costoUsado, monedaUsada);
                  }}
                >
                  Es ese: marcarlo recibido
                </Button>
              ) : null}
            </li>
          );
        })}
      </ul>
      <p className="mt-2 text-xs">
        Si es el del cliente, marcalo recibido: no suma stock libre y queda para entregar.
      </p>
      <div className="mt-2">
        <Casilla
          label="Es otra unidad, va al stock"
          marcado={esOtraUnidad}
          onMarcadoChange={onEsOtraUnidadChange}
        />
      </div>
    </Aviso>
  );
}

/**
 * El aviso que convierte "cargue una compra" en "cambie el precio de venta".
 *
 * Va con el numero de antes y el de despues, no con una advertencia generica:
 * "esto puede modificar precios" no le dice a nadie si el iPhone pasa de
 * $ 1.300.000 a $ 1.310.000 o a $ 1.900.000.
 */
function AvisoImpacto({
  impacto,
  cotizacion,
}: {
  impacto: NonNullable<ReturnType<typeof impactoDelIngreso>>;
  cotizacion: number;
}): JSX.Element {
  const comparable = costoComparable(
    impacto.costoNuevo,
    impacto.monedaNueva,
    impacto.monedaAnterior,
    cotizacion,
  );
  const subeCosto = comparable > impacto.costoAnterior;

  if (impacto.precioCongeladoPorManual) {
    return (
      <Aviso tono="warning" compacto titulo="El costo cambia, el precio no">
        Este producto tiene el precio puesto a mano (
        <Importe monto={impacto.precioAntes} moneda={impacto.monedaPrecio} tamano="sm" />
        ), así que no se repricia. Lo que cambia es el margen: el costo pasa de{" "}
        <Importe monto={impacto.costoAnterior} moneda={impacto.monedaAnterior} tamano="sm" /> a{" "}
        <Importe monto={impacto.costoNuevo} moneda={impacto.monedaNueva} tamano="sm" />.
      </Aviso>
    );
  }

  // El costo cambia y el redondeo deja el precio donde estaba: se avisa del
  // costo, sin «sube el precio (+0,00 %)».
  if (impacto.diferencia === 0) {
    return (
      <Aviso
        tono="warning"
        compacto
        titulo="El costo cambia, el precio no"
        data-testid="aviso-impacto-precio"
      >
        El costo de reposición pasa de{" "}
        <Importe monto={impacto.costoAnterior} moneda={impacto.monedaAnterior} tamano="sm" /> a{" "}
        <Importe monto={impacto.costoNuevo} moneda={impacto.monedaNueva} tamano="sm" />. Con el
        redondeo, el precio de venta sigue en{" "}
        <Importe monto={impacto.precioDespues} moneda={impacto.monedaPrecio} tamano="sm" />.
      </Aviso>
    );
  }

  return (
    <Aviso
      tono="warning"
      compacto
      titulo="Esto cambia el precio de venta"
      data-testid="aviso-impacto-precio"
    >
      <div className="space-y-1">
        <p>
          El costo de reposición pasa de{" "}
          <Importe monto={impacto.costoAnterior} moneda={impacto.monedaAnterior} tamano="sm" /> a{" "}
          <Importe monto={impacto.costoNuevo} moneda={impacto.monedaNueva} tamano="sm" />
          {impacto.monedaNueva !== impacto.monedaAnterior ? (
            <>
              {" "}
              (<Importe monto={comparable} moneda={impacto.monedaAnterior} tamano="sm" />)
            </>
          ) : null}
          , así que {subeCosto ? "sube" : "baja"} el precio de venta:
        </p>
        <p className="flex flex-wrap items-center gap-2">
          <span className="line-through">
            <Importe monto={impacto.precioAntes} moneda={impacto.monedaPrecio} tamano="sm" />
          </span>
          <ArrowRight className="size-3.5 text-muted-foreground" aria-hidden="true" />
          <Importe
            monto={impacto.precioDespues}
            moneda={impacto.monedaPrecio}
            className="font-semibold"
          />
          {/* Un porcentaje no es plata: sin color, con el signo explicito. */}
          <span className="tabular-nums text-muted-foreground">
            ({impacto.diferencia >= 0 ? "+" : "−"}
            {formatearNumero(Math.abs(impacto.diferenciaPct))} %)
          </span>
        </p>
        <p className="text-muted-foreground">
          Si esto es un ajuste de inventario y no una compra, destildá la casilla de arriba.
        </p>
      </div>
    </Aviso>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Salida
// ─────────────────────────────────────────────────────────────────────────────

export interface DialogSalidaProps {
  abierto: boolean;
  onAbiertoChange: (abierto: boolean) => void;
  productos: ReadonlyArray<Producto>;
  productoId?: number | null;
  /** Para "Retiro de un socio": quien se llevo la mercaderia. */
  socios?: ReadonlyArray<Socio>;
  /** El motivo con que se abre. Desde Socios es "Retiro de un socio"; si no, merma. */
  tipoInicial?: TipoSalida;
  /**
   * Desde Socios: el motivo ya esta dicho por el boton que se toco. No se
   * ofrece cambiarlo, y se callan el aviso de ventas y la referencia, que son
   * preguntas de un ajuste de inventario.
   */
  motivoFijo?: boolean;
  /** El socio con que se abre. Solo si quien abre ya lo sabe; si no, ninguno. */
  socioInicial?: number;
  guardando?: boolean;
  onConfirmar: (datos: SalidaStock) => void;
}

export function DialogSalida({
  abierto,
  onAbiertoChange,
  productos,
  productoId = null,
  socios = [],
  tipoInicial = "MERMA",
  motivoFijo = false,
  socioInicial,
  guardando = false,
  onConfirmar,
}: DialogSalidaProps): JSX.Element {
  const [paso, setPaso] = useState<"carga" | "revision">("carga");
  const [producto, setProducto] = useState<Producto | null>(null);
  const [tipo, setTipo] = useState<TipoSalida>("MERMA");
  const [cantidad, setCantidad] = useState<number | null>(null);
  const [socioId, setSocioId] = useState("");
  const [referencia, setReferencia] = useState("");
  const [observaciones, setObservaciones] = useState("");

  useEffect(() => {
    if (!abierto) return;
    setPaso("carga");
    setProducto(productoId === null ? null : (productos.find((p) => p.id === productoId) ?? null));
    setTipo(tipoInicial);
    setCantidad(null);
    setSocioId(socioInicial ? String(socioInicial) : "");
    setReferencia("");
    setObservaciones("");
  }, [abierto, productoId, productos, tipoInicial, socioInicial]);

  const infoTipo = TIPOS_SALIDA.find((t) => t.tipo === tipo);
  const resultante = producto
    ? stockResultante(aNumero(producto.stock_actual), cantidad ?? 0, -1)
    : null;
  const esRetiro = tipo === "RETIRO_SOCIO";
  const socio = socios.find((s) => String(s.id) === socioId);
  const superaElStock =
    producto !== null &&
    cantidad !== null &&
    salidaSuperaElStock(tipo, aNumero(producto.stock_actual), cantidad);
  // Un retiro de una ficha sin costo se le cargaba al socio por US$ 0, y en
  // Socios quedaba la mercaderia regalada sin que nadie lo decidiera. Se avisa
  // y no se deja seguir hasta cargar el costo.
  const retiroSinCosto = esRetiro && producto !== null && !(aNumero(producto.costo) > 0);
  const listo = Boolean(
    producto &&
    cantidad !== null &&
    cantidad > 0 &&
    (!esRetiro || socio) &&
    !superaElStock &&
    !retiroSinCosto,
  );
  // Lo que se le va a cargar al socio. Es una muestra: el numero que vale lo
  // congela la base al guardar, con el costo y la cotizacion de ese momento.
  const costoRetiro =
    esRetiro && producto && cantidad !== null ? aNumero(producto.costo) * cantidad : null;

  function confirmar(): void {
    if (!producto || cantidad === null) return;
    onConfirmar({
      producto_id: producto.id,
      cantidad,
      tipo,
      ...(esRetiro && socio ? { socio_id: socio.id } : {}),
      ...(referencia.trim() ? { referencia: referencia.trim() } : {}),
      ...(observaciones.trim() ? { observaciones: observaciones.trim() } : {}),
    });
  }

  return (
    <Dialog open={abierto} onOpenChange={onAbiertoChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {paso === "revision"
              ? "Revisá antes de guardar"
              : motivoFijo
                ? esRetiro
                  ? "Retiro de mercadería"
                  : (infoTipo?.label ?? "Salida")
                : "Ajuste de salida"}
          </DialogTitle>
          <DialogDescription>
            {paso === "revision"
              ? "Esto escribe un movimiento de stock. No se deshace con Ctrl+Z."
              : motivoFijo
                ? infoTipo?.ayuda
                : "Descuenta unidades por mermas, roturas, garantías o un conteo que dio menos."}
          </DialogDescription>
        </DialogHeader>

        <DialogBody>
          {paso === "carga" ? (
            <>
              {/*
                Se dice antes de que la pregunta aparezca. Es la unica salida que
                alguien va a buscar aca y la unica que no se carga aca.
              */}
              {motivoFijo ? null : (
                <Aviso tono="neutral" compacto>
                  Las ventas no se cargan acá. La salida por venta la genera el comprobante en la
                  misma operación; anotarla también como ajuste descontaría el stock dos veces.
                </Aviso>
              )}

              {motivoFijo && esRetiro ? (
                <ChipsSocio socios={socios} valor={socioId} onChange={setSocioId} />
              ) : null}

              <SelectorProducto
                productos={productos}
                seleccionado={producto}
                onSeleccionar={setProducto}
                autoFocus
              />

              {motivoFijo ? null : (
                <Campo label="Motivo de la salida" id="sal-tipo" ayuda={infoTipo?.ayuda}>
                  <Select value={tipo} onValueChange={(v) => setTipo(v as TipoSalida)}>
                    <SelectTrigger id="sal-tipo" aria-label="Motivo de la salida">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TIPOS_SALIDA.map((t) => (
                        <SelectItem key={t.tipo} value={t.tipo}>
                          {t.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Campo>
              )}

              <CampoCantidad
                label="Cantidad que sale"
                valor={cantidad}
                onValorChange={setCantidad}
                sufijo="u."
                requerido
                ayuda={
                  resultante && cantidad !== null && !superaElStock
                    ? `Queda en ${mostrarCantidad(resultante.despues)} unidades.`
                    : undefined
                }
                error={
                  superaElStock && producto
                    ? `Hay ${mostrarCantidad(aNumero(producto.stock_actual))} u. en stock: no pueden salir más de las que hay. Si el conteo dio distinto, cargalo como ajuste de inventario.`
                    : undefined
                }
              />

              {esRetiro ? (
                <>
                  {motivoFijo ? null : (
                    <ChipsSocio socios={socios} valor={socioId} onChange={setSocioId} />
                  )}
                  {retiroSinCosto ? (
                    <Aviso tono="warning" compacto>
                      Esta ficha no tiene costo: el retiro figuraría en US$ 0. Cargá el costo del
                      producto antes de registrarlo.
                    </Aviso>
                  ) : costoRetiro !== null && producto ? (
                    <p className="text-xs text-muted-foreground">
                      Se le carga al costo:{" "}
                      <Importe monto={costoRetiro} moneda={producto.costo_moneda} tamano="sm" />
                      {producto.costo_moneda === "ARS"
                        ? ", que se pasa a dólares con la cotización del día."
                        : "."}
                    </p>
                  ) : null}
                </>
              ) : null}

              <div className={cn("grid grid-cols-1 gap-4", !motivoFijo && "sm:grid-cols-2")}>
                {motivoFijo ? null : (
                  <Campo label="Referencia" id="sal-ref">
                    <Input
                      id="sal-ref"
                      value={referencia}
                      onChange={(e) => setReferencia(e.target.value)}
                      placeholder="Nº de orden, remito, etc."
                      autoComplete="off"
                    />
                  </Campo>
                )}
                <Campo label="Observaciones" id="sal-obs">
                  <Input
                    id="sal-obs"
                    value={observaciones}
                    onChange={(e) => setObservaciones(e.target.value)}
                    placeholder="Qué pasó"
                    autoComplete="off"
                  />
                </Campo>
              </div>
            </>
          ) : (
            <ResumenMovimiento
              producto={producto?.nombre ?? ""}
              operacion={`Salida · ${infoTipo?.label ?? tipo}${socio && esRetiro ? ` · ${socio.nombre}` : ""}`}
              cantidad={cantidad ?? 0}
              signo={-1}
              resultante={resultante}
              cargo={
                esRetiro && socio && producto && costoRetiro !== null
                  ? { socio: socio.nombre, monto: costoRetiro, moneda: producto.costo_moneda }
                  : undefined
              }
              avisoNegativo={
                tipo === "AJUSTE"
                  ? undefined
                  : "El stock queda en negativo. Revisá que la cantidad sea la correcta."
              }
            />
          )}
        </DialogBody>

        <DialogFooter>
          {paso === "carga" ? (
            <>
              <Button type="button" variant="ghost" onClick={() => onAbiertoChange(false)}>
                Cancelar
              </Button>
              <Button type="button" disabled={!listo} onClick={() => setPaso("revision")}>
                Revisar
              </Button>
            </>
          ) : (
            <>
              <Button type="button" variant="ghost" onClick={() => setPaso("carga")}>
                <ArrowLeft aria-hidden="true" />
                Volver
              </Button>
              <Button type="button" cargando={guardando} onClick={confirmar}>
                Confirmar salida
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Conteo
// ─────────────────────────────────────────────────────────────────────────────

/**
 * El movimiento que sale de un conteo. Lo decide la diferencia, no la persona.
 *
 * El `reparto` viaja solo cuando lo contado es la FILA DE UN MODELO y aparecio
 * de mas: son unidades que estan en el local y hay que decir de que color, o
 * quedan colgadas del modelo sin color. Contar de MENOS no lo lleva: de cual
 * color falta es otra pregunta, y la salida va contra la fila como siempre.
 */
export type MovimientoDeConteo =
  | { sentido: "ingreso"; datos: IngresoStock; reparto?: ReadonlyArray<UnidadesDeColor> }
  | { sentido: "salida"; datos: SalidaStock };

export interface DialogConteoProps {
  abierto: boolean;
  onAbiertoChange: (abierto: boolean) => void;
  productos: ReadonlyArray<Producto>;
  productoId?: number | null;
  /** Abierto desde la ficha de un producto: ese es el que se cuenta, y no se cambia. */
  productoFijo?: boolean;
  /**
   * La lista PLANA, con las fichas de color. Hace falta para contar la fila de
   * un modelo sellado: sus unidades viven en las fichas de cada color. Sin
   * ella se buscan en `productos`.
   */
  todos?: ReadonlyArray<Producto>;
  guardando?: boolean;
  onConfirmar: (movimiento: MovimientoDeConteo) => void;
}

/**
 * "Contá el estante y decime cuántas hay."
 *
 * ES EL DIALOGO DE UNA SOLA PANTALLA, Y ESO ES DELIBERADO. El ingreso y la
 * salida tienen paso de revision porque el numero que se escribe (la cantidad
 * que se mueve) no es el que importa (el que queda): hay que mostrar el segundo
 * antes de guardar. Aca el numero que se escribe ES el que queda. Mandar a
 * revisar el mismo numero que se acaba de tipear no agrega control, agrega un
 * toque.
 *
 * Lo que si se muestra, en vivo, es el movimiento que la diferencia va a
 * escribir: nadie deberia guardar un ajuste sin haber leido "faltan 3".
 */
export function DialogConteo({
  abierto,
  onAbiertoChange,
  productos,
  productoId = null,
  productoFijo = false,
  todos,
  guardando = false,
  onConfirmar,
}: DialogConteoProps): JSX.Element {
  const [producto, setProducto] = useState<Producto | null>(null);
  const [contado, setContado] = useState<number | null>(null);
  const [observaciones, setObservaciones] = useState("");
  const [reparto, setReparto] = useState<ReadonlyArray<UnidadesDeColor>>([]);

  useEffect(() => {
    if (!abierto) return;
    setProducto(productoId === null ? null : (productos.find((p) => p.id === productoId) ?? null));
    setContado(null);
    setObservaciones("");
    setReparto([]);
  }, [abierto, productoId, productos]);

  /*
   * LA FILA DE UN MODELO SELLADO NO TIENE LAS UNIDADES: LAS TIENEN SUS COLORES.
   *
   * Antes «El sistema dice» mostraba el stock de la fila, que es la lista de
   * precios y esta siempre en cero. Con un Black y un Teal cargados, contar 3
   * escribia +3 y cada color recibia lo repartido encima de lo que ya tenia:
   * Black 3, Teal 2. Ahora lo que el sistema dice es la fila mas sus colores,
   * y lo que se reparte es solo lo que aparecio de mas.
   */
  const fichasDeColor = useMemo(
    () =>
      producto && esIphoneSellado(producto) && !producto.color
        ? fichasDeColorDelModelo(producto, todos ?? productos).filter(
            (f) => aNumero(f.stock_actual) !== 0,
          )
        : [],
    [producto, todos, productos],
  );
  const enFichas = fichasDeColor.reduce((t, f) => t + aNumero(f.stock_actual), 0);
  const enSistema = producto ? aNumero(producto.stock_actual) + enFichas : null;
  const ajuste =
    enSistema !== null && contado !== null ? ajustePorConteo(enSistema, contado) : null;

  /*
   * Faltan unidades y estan repartidas por color: de cual falta no se sabe
   * desde la fila del modelo. Sacarlas de la fila la dejaba en negativo y los
   * colores quedaban como estaban. Se cuenta cada color desde su fila.
   */
  const faltaDeUnColor = fichasDeColor.length > 0 && ajuste?.sentido === "salida";

  // Contar no puede dar menos que cero. Es un error de tipeo (un menos de mas),
  // no una decision: se frena antes de convertirlo en una salida enorme.
  const imposible = contado !== null && contado < 0;

  /*
   * APARECIERON EQUIPOS Y HAY QUE DECIR DE QUE COLOR SON.
   *
   * Contar la fila de un modelo y que de mas es exactamente como entraron las
   * unidades sin color del 18 Pro: el conteo escribia el ajuste contra la fila
   * y nadie preguntaba nada. Ahora se pregunta, y es el mejor momento posible
   * para hacerlo — el equipo esta en la mano, contandolo.
   *
   * Contar de MENOS no pregunta: de cual color falta es otra pregunta, y la
   * salida sale de la fila como salia.
   */
  const coloresModelo = producto ? coloresDelModelo(producto.nombre) : [];
  const puedeRepartir =
    producto !== null &&
    esIphoneSellado(producto) &&
    !producto.color &&
    coloresModelo.length > 0 &&
    ajuste?.sentido === "ingreso";

  /*
   * UN PRECIO DE REFERENCIA NO SE CUENTA PARA ARRIBA.
   *
   * Es el precio de un modelo usado, no un teléfono. Contar «hay uno» contra él
   * es como entraron en septiembre cuatro equipos del local sin su ficha: sin
   * batería, sin IMEI, colgados del precio. La base lo rechaza; acá se dice
   * antes y se dice qué hacer. Contar para abajo sí se deja: es limpiar eso.
   */
  const referenciaConEquipo =
    producto !== null && esReferenciaUsada(producto) && ajuste?.sentido === "ingreso";

  const listo = Boolean(
    ajuste &&
    ajuste.sentido !== "sin_cambio" &&
    !imposible &&
    !referenciaConEquipo &&
    !faltaDeUnColor &&
    (!puedeRepartir || faltaParaRepartir(reparto, ajuste.cantidad) === null),
  );

  function confirmar(): void {
    if (!producto || !ajuste || ajuste.sentido === "sin_cambio" || imposible) return;
    const obs = observaciones.trim();
    const comun = {
      producto_id: producto.id,
      cantidad: ajuste.cantidad,
      tipo: "AJUSTE" as const,
      referencia: "CONTEO",
      ...(obs ? { observaciones: obs } : {}),
    };
    onConfirmar(
      ajuste.sentido === "ingreso"
        ? // `actualiza_costo: false` explicito: sin el, el backend toma el default
          // (true) y un conteo terminaria pudiendo tocar precios. No manda costo,
          // asi que hoy no repriciaria igual; el flag es el cinturon.
          {
            sentido: "ingreso",
            datos: { ...comun, actualiza_costo: false },
            ...(puedeRepartir ? { reparto: soloLasQueVan(reparto) } : {}),
          }
        : { sentido: "salida", datos: comun },
    );
  }

  return (
    <Dialog open={abierto} onOpenChange={onAbiertoChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Corregir stock contando</DialogTitle>
          <DialogDescription>
            Contá lo que hay en el estante y poné ese número. El ajuste lo hace el sistema.
          </DialogDescription>
        </DialogHeader>

        <DialogBody>
          {productoFijo && producto ? (
            <Card variant="muted" padding="normal">
              <p className="truncate text-sm font-medium">{producto.nombre}</p>
              <p className="truncate text-xs tabular-nums text-muted-foreground">
                {producto.codigo_interno}
              </p>
            </Card>
          ) : (
            <SelectorProducto
              productos={productos}
              seleccionado={producto}
              onSeleccionar={setProducto}
              label="¿Qué producto contaste?"
              autoFocus
            />
          )}

          {producto ? (
            <>
              <Card
                variant="muted"
                padding="compacto"
                className="flex items-baseline justify-between gap-4"
              >
                <TituloSeccion nivel="etiqueta" as="span">
                  El sistema dice
                </TituloSeccion>
                {/* Unidades, no plata: sin chip ni color de moneda. */}
                <span className="text-right">
                  <span className="block text-lg font-semibold tabular-nums">
                    {mostrarCantidad(enSistema ?? 0)} u.
                  </span>
                  {fichasDeColor.length > 0 ? (
                    <span className="block text-xs text-muted-foreground">
                      {fichasDeColor
                        .map((f) => `${f.color} ${mostrarCantidad(aNumero(f.stock_actual))}`)
                        .join(" · ")}
                    </span>
                  ) : null}
                </span>
              </Card>

              <CampoCantidad
                label="¿Cuántas contaste?"
                valor={contado}
                onValorChange={setContado}
                sufijo="u."
                requerido
                autoFocus={productoFijo}
                ayuda="El número real, el que hay en el estante. Si contaste cero, poné cero."
              />

              {imposible ? (
                <Aviso tono="destructive" compacto>
                  No se pueden contar menos de cero unidades. Revisá el número.
                </Aviso>
              ) : referenciaConEquipo ? (
                <Aviso tono="warning" compacto>
                  Esto es un precio de referencia, no un teléfono: no lleva stock. Si hay un equipo
                  en el local, cargalo con Ingreso, «iPhone usado», con su batería y su IMEI.
                </Aviso>
              ) : faltaDeUnColor && ajuste ? (
                <Aviso tono="warning" compacto>
                  Faltan {mostrarCantidad(ajuste.cantidad)} u., pero las unidades de este modelo
                  están cargadas por color. Contá cada color desde su fila para que la salida sea
                  del que falta.
                </Aviso>
              ) : ajuste ? (
                <ResultadoDelConteo ajuste={ajuste} />
              ) : null}

              {/*
                Va despues del resultado: primero se lee «sobran 2» y recien
                ahi tiene sentido preguntar de que color son esas dos.
              */}
              {puedeRepartir && ajuste?.sentido === "ingreso" ? (
                <GrupoCampo
                  label="¿Cuántas de cada color?"
                  ayuda="Esa fila es la lista de precios del modelo. Las unidades que aparecieron entran a la ficha de su color, que se crea sola si todavía no existe."
                  requerido
                >
                  <RepartoPorColor
                    colores={coloresModelo}
                    total={ajuste.cantidad}
                    reparto={reparto}
                    onRepartoChange={setReparto}
                  />
                </GrupoCampo>
              ) : null}

              <Campo label="Observaciones" id="cont-obs">
                <Input
                  id="cont-obs"
                  value={observaciones}
                  onChange={(e) => setObservaciones(e.target.value)}
                  placeholder="Opcional: qué pasó"
                  autoComplete="off"
                />
              </Campo>
            </>
          ) : null}
        </DialogBody>

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => onAbiertoChange(false)}>
            Cancelar
          </Button>
          <Button type="button" cargando={guardando} disabled={!listo} onClick={confirmar}>
            Guardar el conteo
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Que movimiento va a escribir esta diferencia, dicho antes de escribirlo.
 *
 * Va con el numero y con el sentido en palabras ("sobran 3", "faltan 3") porque
 * un "+3" no le dice a nadie si el sistema estaba corto o pasado.
 */
function ResultadoDelConteo({ ajuste }: { ajuste: AjustePorConteo }): JSX.Element {
  if (ajuste.sentido === "sin_cambio") {
    return (
      <Aviso tono="success" compacto data-testid="conteo-coincide">
        El conteo coincide con el sistema. No hay nada que corregir.
      </Aviso>
    );
  }

  const entra = ajuste.sentido === "ingreso";
  return (
    <Aviso
      tono="warning"
      compacto
      data-testid="conteo-ajuste"
      titulo={
        entra
          ? `Hay ${mostrarCantidad(ajuste.cantidad)} u. de más que en el sistema`
          : `Faltan ${mostrarCantidad(ajuste.cantidad)} u. respecto del sistema`
      }
    >
      <div className="space-y-1">
        <p className="flex flex-wrap items-center gap-2 tabular-nums">
          <span className="text-muted-foreground">{mostrarCantidad(ajuste.antes)}</span>
          <ArrowRight className="size-3.5 text-muted-foreground" aria-hidden="true" />
          <span className="text-sm font-semibold">{mostrarCantidad(ajuste.contado)} u.</span>
        </p>
        <p className="text-muted-foreground">
          Se guarda como {entra ? "un ingreso" : "una salida"} por ajuste de inventario, con tu
          usuario y la fecha. No toca el costo ni el precio de venta.
        </p>
      </div>
    </Aviso>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function ResumenMovimiento({
  producto,
  operacion,
  cantidad,
  signo,
  resultante,
  cargo,
  porColor,
  avisoNegativo = "El stock queda en negativo. Es válido —significa que falta ingresar una compra— pero revisá que la cantidad sea la correcta.",
}: {
  producto: string;
  operacion: string;
  cantidad: number;
  signo: 1 | -1;
  resultante: { antes: number; despues: number; quedaNegativo: boolean } | null;
  /**
   * Lo que se dice si queda en negativo. «Falta ingresar una compra» es la
   * lectura de un ajuste; en otra salida no significa nada.
   */
  avisoNegativo?: string;
  /**
   * En un retiro de socio: cuanto se le carga. La revision no mostraba ningun
   * importe, y era el ultimo lugar para ver que se le cargaba otra cosa.
   */
  cargo?: { socio: string; monto: number; moneda: Moneda };
  /**
   * El ingreso repartido por color: las unidades van a la ficha de cada color,
   * no a la fila del modelo. La revisión mostraba el stock de esa fila («0 →
   * 2») y no qué colores entraban, que es justo lo que se eligió.
   */
  porColor?: ReadonlyArray<{ color: string; cantidad: number }>;
}): JSX.Element {
  return (
    <Card variant="muted" padding="normal" className="space-y-3">
      <p className="text-sm font-medium">{operacion}</p>

      <dl className="space-y-2 text-sm">
        <FilaResumen termino="Producto" valor={producto} />
        <FilaResumen
          termino="Cantidad"
          valor={`${signo > 0 ? "+" : "−"} ${mostrarCantidad(cantidad)} u.`}
        />
        {porColor && porColor.length > 0 ? (
          <FilaResumen
            termino="Por color"
            valor={porColor.map((c) => `${mostrarCantidad(c.cantidad)} ${c.color}`).join(" · ")}
          />
        ) : resultante ? (
          <FilaResumen
            termino="Stock"
            valor={
              <span className="inline-flex items-center gap-2 tabular-nums">
                <span className="text-muted-foreground">{mostrarCantidad(resultante.antes)}</span>
                <ArrowRight className="size-3.5 text-muted-foreground" aria-hidden="true" />
                <span
                  className={cn(
                    "font-semibold",
                    resultante.quedaNegativo ? "text-destructive" : "",
                  )}
                >
                  {mostrarCantidad(resultante.despues)}
                </span>
              </span>
            }
          />
        ) : null}
        {cargo ? (
          <FilaResumen
            termino={`Se le carga a ${cargo.socio}`}
            valor={<Importe monto={cargo.monto} moneda={cargo.moneda} tamano="sm" />}
          />
        ) : null}
      </dl>

      {resultante?.quedaNegativo ? (
        <Aviso tono="destructive" compacto>
          {avisoNegativo}
        </Aviso>
      ) : null}
    </Card>
  );
}

function FilaResumen({ termino, valor }: { termino: string; valor: React.ReactNode }): JSX.Element {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <dt className="shrink-0">
        <TituloSeccion nivel="etiqueta" as="span">
          {termino}
        </TituloSeccion>
      </dt>
      <dd className="min-w-0 text-right text-sm">{valor}</dd>
    </div>
  );
}
